
import type { QueryRankingToolConfigProvider } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { EpbSelectOption } from '../../../components/shared/epb-select'
import type { IToolingFormComposableProps } from '../../../composables'

import type { Equalize } from '../../../modules/equalize'

import type { EqualizeDomainService } from '../../../services/interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { UiConfirm, UiTextbox } from '../../../components/keen-ui'
import BoostingFeatureContainer from '../../../components/search-equalize/boosting-feature-container.component.vue'
import FunctionScoreFeatureContainer from '../../../components/search-equalize/function-score-feature-container.component.vue'
import SemanticMatchFeatureContainer from '../../../components/search-equalize/semantic-match-feature-container.component.vue'
import SponsoredScoreFeatureContainer from '../../../components/search-equalize/sponsored-score-feature-container.component.vue'
import TextualMatchFeatureContainer from '../../../components/search-equalize/textual-match-feature-container.component.vue'
import { EpbBadge } from '../../../components/shared/epb-badge'
import { EpbButton } from '../../../components/shared/epb-button'
import { EpbSelect } from '../../../components/shared/epb-select'
import { EpbTextbox } from '../../../components/shared/epb-textbox'
import ToolingFormLayout from '../../../components/tooling/tooling-form-layout.component.vue'
import ToolingPreview from '../../../components/tooling/tooling-preview.component.vue'
import TransitionSize from '../../../components/transition-size.component.vue'
import { useEndpoints, useToolingForm } from '../../../composables'

import { CONTAINER } from '../../../config'

import { TYPES } from '../../../config/inversify.types'
import { EndpointContextEnum } from '../../../enums'
import { FeatureIdEnum } from '../../../enums/feature-id.enum'

import { ROUTER_CONSTS } from '../../../router/router.consts'
import { computedFn } from './computed'
import { dataFn } from './data'
import { methodsFn } from './methods'

export default defineComponent({
  name: 'EqualizeForm',
  components: {
    BoostingFeatureContainer,
    EpbButton,
    EpbBadge,
    EpbTextbox,
    EpbSelect,
    FunctionScoreFeatureContainer,
    SemanticMatchFeatureContainer,
    SponsoredScoreFeatureContainer,
    TextualMatchFeatureContainer,
    ToolingFormLayout,
    ToolingPreview,
    TransitionSize,
    UiConfirm,
    UiTextbox,
  },
  setup(props: IToolingFormComposableProps, ctx: SetupContext) {
    const {
      proxy: { $epb, $route },
    } = getCurrentInstance()

    const domainService = CONTAINER.get<EqualizeDomainService>(TYPES.EqualizeDomainService)
    const queryRankingToolConfig = CONTAINER.get<QueryRankingToolConfigProvider>(
      TYPES.QueryRankingToolConfigProvider,
    )()

    const baseClass = 'epb-equalize-form'

    const pivotsExtraConfig = {
      default: {
        mandatory: false,
        multiselect: true,
      },
    }

    const messages = $epb.messages.tools.equalize
    const updateMode = $route.name === ROUTER_CONSTS.routes[FeatureIdEnum.EQUALIZE].update

    const toolingForm = useToolingForm<Equalize>(props, ctx, {
      pivotsExtraConfig,
      messages,
      updateMode,
    })

    const { endpoints: configEndpoints } = useEndpoints()

    const endpointId = ref(toolingForm.item.value.endpointId ?? DomainEnum.SEARCH)
    const confirmEndpointIdChangeDialog = ref()

    const defaultRows = computed(() => toolingForm.toolConfig.value.allDefaultEndpoints || [])

    const endpoints = computed(() => {
      return configEndpoints.value.filter(endpoint =>
        defaultRows.value.some(row => row.endpointId === endpoint.value),
      )
    })

    const browseFieldOptions = computed<Array<EpbSelectOption>>(
      () =>
        queryRankingToolConfig.browseFields?.map(browseField => ({
          value: browseField.id,
          label: browseField.displayName,
          isCategory: browseField.isCategory,
        })) ?? [],
    )

    const context = computed(() => {
      const contextString =
        endpoints.value.find(endpoint => endpoint.value === endpointId.value)?.context ?? 'query'
      return (
        Object.values(EndpointContextEnum).find(value => value === contextString) ??
        EndpointContextEnum.QUERY
      )
    })

    const equalizeFormData = dataFn()
    const equalizeFormComputed = computedFn(props, ctx, {
      toolingForm,
      equalizeFormData,
      messages,
      context,
    })
    const equalizeFormMethods = methodsFn(props, ctx, { toolingForm })

    const updateFeaturesForEndpoint = (selectedEndpointId: string): void => {
      const selectedConfig = defaultRows.value.find(
        config => config.endpointId === selectedEndpointId,
      )
      if (selectedConfig && selectedConfig.features) {
        toolingForm.item.value.defaultFeatures = selectedConfig.features
        domainService.mergeWithDefault(
          toolingForm.item.value.features,
          toolingForm.item.value.defaultFeatures,
        )
      }
    }

    const resetFeaturesToDefaults = (): void => {
      toolingForm.item.value.features = { ...domainService.getEmptyInstance().features }
    }

    toolingForm.initItem().then(() => {
      const isCreationMode = !updateMode && !toolingForm.isCloneMode()
      if (isCreationMode) {
        const defaultEndpointId = toolingForm.toolConfig.value.default?.endpointId
        resetFeaturesToDefaults()
        if (defaultEndpointId) {
          toolingForm.item.value.endpointId = defaultEndpointId
        }
      }
      updateFeaturesForEndpoint(toolingForm.item.value.endpointId)
    })

    const selectEndpoint = (value: string): void => {
      const newEndpointContext =
        endpoints.value.find(endpoint => endpoint.value === value)?.context ??
        EndpointContextEnum.QUERY

      const hasFeaturesModified = !domainService.isEveryFeatureByDefault(
        toolingForm.item.value.features,
        toolingForm.item.value.defaultFeatures,
      )

      if (newEndpointContext !== context.value && hasFeaturesModified) {
        endpointId.value = value
        confirmEndpointIdChangeDialog.value?.open()
      } else {
        toolingForm.item.value.endpointId = value
        updateFeaturesForEndpoint(value)
      }
    }

    const confirmEndpointIdChange = (): void => {
      resetFeaturesToDefaults()
      toolingForm.item.value.endpointId = endpointId.value
      updateFeaturesForEndpoint(endpointId.value)
    }

    watch(
      () => toolingForm.item.value.endpointId,
      value => {
        endpointId.value = value
        updateFeaturesForEndpoint(value)
      },
      { immediate: true },
    )

    return {
      ...toolingForm,
      ...equalizeFormData,
      ...equalizeFormComputed,
      ...equalizeFormMethods,
      EndpointContextEnum,
      baseClass,
      messages,
      updateMode,
      endpointId,
      endpoints,
      confirmEndpointIdChangeDialog,
      context,
      selectEndpoint,
      confirmEndpointIdChange,
      browseFieldOptions,
    }
  },
})
